package com.pincer.core.functions

import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject

/*
JSON Merge Patch (RFC 7396), generically, over kotlinx.serialization's JsonElement.

A merge patch is a JSON object shaped like the document it changes:
 - a key that's absent means "no change"
 - a key whose value is null means "delete this"
 - a nested object is itself a merge patch for the object at that key
 - anything else (string, number, boolean, array) replaces the value at that key

This is almost exactly the shape of our hand-written PincerPatch, except that PincerPatch can only delete
entries from maps, while here any field can be deleted (so a scalar can be cleared back to its default).

Prototype for the patch_and_sync evaluation (September 2026). Not wired in yet.
*/
object MergePatch {

    /** Applies [patch] to [target], returning the result. Neither input is modified. */
    fun apply(target: JsonElement?, patch: JsonElement): JsonElement {
        if (patch !is JsonObject) return patch
        val result = (target as? JsonObject)?.toMutableMap() ?: mutableMapOf()
        for ((key, value) in patch) {
            if (value is JsonNull) result.remove(key)
            else result[key] = apply(result[key], value)
        }
        return JsonObject(result)
    }

    /**
     * The merge patch that turns [from] into [to], or null if they're the same.
     *
     * Our model's JSON includes nulls for unset optional fields. A merge patch can't tell "set to null" from
     * "delete", and for our model they mean the same thing (decode gives the default), so null and absent are
     * treated as equal here, and nulls are left out of anything newly added.
     */
    fun diff(from: JsonElement?, to: JsonElement?): JsonElement? {
        val fromValue = from.orAbsent()
        val toValue = to.orAbsent()
        if (fromValue == toValue) return null
        if (toValue == null) return JsonNull
        if (fromValue !is JsonObject || toValue !is JsonObject) return withoutNulls(toValue)
        val build = mutableMapOf<String, JsonElement>()
        for (key in fromValue.keys + toValue.keys) {
            val change = diff(fromValue[key], toValue[key])
            if (change != null) build[key] = change
        }
        return if (build.isEmpty()) null else JsonObject(build)
    }

    private fun JsonElement?.orAbsent(): JsonElement? = if (this is JsonNull) null else this

    private fun withoutNulls(element: JsonElement): JsonElement {
        if (element !is JsonObject) return element
        return JsonObject(element.filterValues { it !is JsonNull }.mapValues { withoutNulls(it.value) })
    }

}
